import NiotaClassicApiService from '@/services/niotaClassicApiService';
import debounce from 'lodash/debounce';
import listMixin from '../listMixin';
import { mapState, mapGetters } from 'vuex';
import { userItemsPerListMixin } from '@/utils/vuex';
const defaultItemsPerPage = 10;
const defaultTableOptions = {
  sortBy: ['id'],
  sortDesc: [false],
  page: 1,
  itemsPerPage: defaultItemsPerPage
};
export default {
  name: 'NiotaClassicDevicetypesList',
  mixins: [listMixin, userItemsPerListMixin],
  props: {
    accountId: {
      type: Boolean,
      required: false,
      default: false
    },
    findCertified: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function () {
    return {
      lastSearch: '',
      isInitialSearch: false,
      disableFetchOnCreatedMixin: true,
      deletePrompt: false,
      deleteItemData: null,
      tableOptions: {
        ...defaultTableOptions
      }
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions']),
    ...mapGetters({
      sessionUserId: 'authentication/userId'
    }),
    tableLocalizations() {
      return {
        headers: [{
          text: this.$i18n_t('NiotaClassic.Common.table.id'),
          value: 'id',
          align: 'right'
        }, {
          value: 'image',
          sortable: false
        }, {
          text: this.$i18n_t('NiotaClassic.Common.table.title'),
          value: 'title'
        }, {
          text: this.$i18n_t('NiotaClassic.DeviceTypes.table.access'),
          value: 'visibility'
        }, {
          text: this.$i18n_t('Common.tenant'),
          value: 'accountname'
        }, {
          text: this.$i18n_t('NiotaClassic.DeviceTypes.table.parser'),
          value: 'parser.type',
          sortable: false
        }, {
          text: this.$i18n_t('NiotaClassic.Common.table.createdAt'),
          value: 'createdAt'
        }, ...(this.isIotHubManager ? [{
          align: 'end',
          sortable: false,
          width: '78'
        }] : [])],
        tableFooterProps: {
          itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
          itemsPerPageOptions: [defaultItemsPerPage, 20, 50]
        }
      };
    }
  },
  watch: {
    accountId: debounce(function () {
      // Items have already been requested
      if (this.isFetching) {
        return;
      }
      this.setAccountFilterParams();
      this.fetchList(true);
    }, 500),
    findCertified: debounce(function () {
      // Items have already been requested
      if (this.isFetching) {
        return;
      }
      this.setCertifiedFilterParams();
      this.fetchList(true);
    }, 500)
  },
  created() {
    const page = parseInt(this.$route.query.page, 10) || defaultTableOptions.page;
    const search = this.$route.query.search || '';
    if (search) {
      this.isInitialSearch = true;
      this.searchItem = search;
    }
    this.tableOptions.page = page;
  },
  methods: {
    getVisibilityDisplayString(visbility) {
      return this.$i18n_t('NiotaClassic.DeviceTypes.visibility')[visbility] || '';
    },
    getParserDisplayString(parser) {
      return this.$i18n_t('NiotaClassic.DeviceTypes.form.parser.parserTypes')[parser] || this.$i18n_t('NiotaClassic.DeviceTypes.form.parser.parserTypes.unset');
    },
    editItem(item) {
      this.$router.push(`/niota-classic/device-types/${item.id}/details?edit`);
    },
    deleteItem() {
      this.deletePrompt = false;
      NiotaClassicApiService.deviceTypesDelete(this.deleteItemData).then(() => {
        this.deleteItemData = null;
        this.$snack(this.$i18n_t('NiotaClassic.DeviceTypes.snacks.deleteSuccess'), 'success');
        this.fetchList();
      }).catch(() => {
        this.deleteItemData = null;
        this.$snack(this.$i18n_t('NiotaClassic.DeviceTypes.snacks.deleteFailed'), 'error');
      });
    },
    showDeletePrompt(item) {
      this.deletePrompt = true;
      this.deleteItemData = item;
    },
    updatePagination(e) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          ...{
            page: e.page || defaultTableOptions.page
          },
          ...{
            perPage: e.itemsPerPage || defaultTableOptions.itemsPerPage
          }
        }
      }).catch(error => {
        console.error(error);
      });
    },
    setAccountFilterParams() {
      this.$router.replace({
        query: {
          ...this.$route.query,
          ...{
            filterUsed: this.accountId ? 1 : undefined
          }
        }
      }).catch(error => {
        console.error(error);
      });
    },
    setCertifiedFilterParams() {
      this.$router.replace({
        query: {
          ...this.$route.query,
          ...{
            filterCertified: this.findCertified ? 1 : undefined
          }
        }
      }).catch(error => {
        console.error(error);
      });
    },
    updateSearchParams(searchTerm) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          ...{
            search: searchTerm || undefined
          } // set to undefined on empty string to remove from query params
        }
      }).catch(error => {
        console.error(error);
      });
    },
    fetchList(fromStart) {
      if (this.isFetching) {
        return;
      }
      this.isFetching = true;
      const trimmedSearch = (this.searchItem || '').trim();
      this.updateSearchParams(trimmedSearch);
      if (this.lastSearch !== trimmedSearch) {
        fromStart = true;
        this.lastSearch = trimmedSearch;
        if (!this.isInitialSearch) {
          this.tableOptions = {
            ...this.tableOptions,
            page: 1
          };
        }
        this.isInitialSearch = false;
      }
      if (fromStart) {
        this.tableOptions = {
          ...this.tableOptions,
          page: 1
        };
      }
      const tableOptions = this.tableOptions;
      const {
        sortBy,
        sortDesc,
        page,
        itemsPerPage
      } = tableOptions;
      const sortItem = sortBy && sortBy.length ? sortBy[0] : 'createdAt';
      const doSortDesc = sortDesc && sortDesc.length ? sortDesc[0] : true;
      const sortDirection = doSortDesc ? 'DESC' : 'ASC';
      const usedItemsPerPage = itemsPerPage ? itemsPerPage : defaultItemsPerPage;
      const usedPage = page || 1;
      NiotaClassicApiService.deviceTypesFind(trimmedSearch, this.findCertified, this.accountId, usedPage, usedItemsPerPage, sortItem, sortDirection).then(res => {
        this.list = res.data.devicetypes;
        this.serverItemsLength = res.pagination.itemCount;
      }).catch(() => {
        this.$snack(this.$i18n_t('NiotaClassic.DeviceTypes.snacks.fetchFailed'), 'error');
      }).finally(() => {
        this.isFetching = false;
      });
    }
  }
};